(function(){
  const DOMAIN = window.Shopify.shop;
  const PUBLIC_TOKENS = {
    'loosekid.myshopify.com': 'd181a9adc0ee8fdbdf1d4b54085cba41',
    'loosekid-us.myshopify.com': '80ea1b4db5ef2e49c61301108dc82db0',
    'lskd-sandbox.myshopify.com': '08e89a152cc385e3a831c4d338c5bb73',
  }

  const SORTED_LENGTH = [
    'X Short',
    '4" Short',
    'Mid Short',
    '6" Short',
    'Bike Short',
    '9" Short',
    '3/4 Length',
    '7/8 Length',
    'Full Length',
    'X Long',
    'Tall'
  ];

  const OPTION_GROUPS = [
    { name: "Pockets", tagPrefix: "pockets:" },
    { name: "Cup Size", tagPrefix: "cupsize:" },
    { name: "Bag Size", tagPrefix: "bagsize:" },
    { name: "Towel Size", tagPrefix: "towelsize:" },
    { name: "Short Liner", tagPrefix: "shortliner:" },
    { name: "Waistband", tagPrefix: "waistband:" },
    { name: "Length", tagPrefix: "length:" },
    { name: "Pack Option", tagPrefix: "packoption:" },
  ];

  // Storefront products(query) OR terms: drop plain OOS so first:N isn't wasted on them,
  // but keep notify-me / upcoming colours. Call sites wrap these terms as one group.
  const PRODUCT_AVAILABILITY_TERMS = "available_for_sale:true OR tag:'BACK-IN-STOCK' OR tag:'COMING-SOON'";

  // TODO: should have just used market handle from shopify in the metaobject markets field
  const MARKET_HANDLE = window.Shopify?.marketHandle ?? '';
  const TAG_CONTENT_MARKETS_MAPPING = {
    au: 'AU',
    nz: 'NZ',
    us: 'US',
    uk: 'UK',
    canada: 'CA',
    singapore: 'SG',
    // Mexico is using the US 3PL
    mexico: 'US',
    // Fallback to ROW
    default: 'ROW',
  }
  const TAG_CONTENT_MARKET_CODE = TAG_CONTENT_MARKETS_MAPPING[MARKET_HANDLE] ?? TAG_CONTENT_MARKETS_MAPPING.default;

  class ShopifyStoreFrontAPI {
    constructor() {
      if(typeof ShopifyStorefrontAPIClient === 'undefined') {
          console.error('ShopifyStorefrontAPIClient is not defined');
          return;
      }
      this.client = ShopifyStorefrontAPIClient.createStorefrontApiClient({
        storeDomain: 'https://' + DOMAIN,
        apiVersion: '2025-10',
        publicAccessToken: PUBLIC_TOKENS[DOMAIN]
      })
    }

    /**
     * Fetches products by tag from the Shopify Storefront API and normalises the response for displaying color swatches
     * Availability is filtered in the Storefront query (available_for_sale OR BACK-IN-STOCK OR COMING-SOON)
     * so the first:N page is not filled by plain OOS siblings. The query keeps the current product via id:.
     * We then sort so available products are first, then sold out (BIS / coming-soon)
     * We also sort the products by price descending that puts discounted products in the end
     *
     * @param {string} styleHandle - styleHandle tag to filter products.
     * @param {number} currentProductId - The ID of the currently viewed product to determine the corresponding product details.
     * @param {boolean} isOgProduct - If the product is an OG product.
     * @param includeDetails - If the product details metafield should be included in the response.
     * @param includeVariants - If the product variants should be included in the response.
     * @param {boolean} excludeFutureDrops - If true, products with future dropDateTime are filtered out.
     * @param {boolean} excludeLowAvailability - If true, products where >50% variants are unavailable are filtered out.
     * @return {Promise<Array<{
     *    id: number
     *    available: boolean
     *    compareAtPrice: number
     *    handle: string
     *    featuredImage: string
     *    swatchColor: string
     *    dropDateTime: string
     *    price: number
     *    tags: Array<string>
     * }>>}
     */
    async getSameStyleProducts ({
      styleHandle,
      currentProductId,
      isOgProduct,
      includeDetails = false,
      includeVariants = false,
      excludeFutureDrops = false,
      excludeLowAvailability = false,
      includeCollections = false,
    }) {
      const isFutureDrop = (dropDateTime) => {
        const dropValue = dropDateTime?.value;
        if (!dropValue) return false;

        const parsed = new Date(`${dropValue.replace(' ', 'T')}Z`);
        return !isNaN(parsed) && parsed > new Date();
      };

      const selections = `
        id
        title
        handle
        tags
        description,
        availableForSale
        compareAtPriceRange {
          minVariantPrice {
            amount
          }
        }
        priceRange {
          minVariantPrice {
            amount
            currencyCode
          }
        }
        featuredImage {
          url
        }
        ${includeCollections ? `
        collections(first: 100) {
          nodes {
            handle
          }
        }` : ''}
        ${excludeFutureDrops ? `
          dropDateTime: metafield(key: "datetime", namespace: "drop") {
            value
          }
        ` : ''}
        ${includeDetails ? `
        details: metafield(namespace: "custom", key: "details") {
          value
        }
        ` : ''}
        ${includeVariants || excludeLowAvailability ? `
        variants (first: 25) {
          nodes {
            id
            sku
            title
            availableForSale
            selectedOptions {
              name
              value
            }
          }
        }` : ''}
      `
      const ogClause = isOgProduct ? `tag:'batch:OG'` : `NOT tag:'batch:OG'`;
      const availabilityQuery = `(${PRODUCT_AVAILABILITY_TERMS}${currentProductId ? ` OR id:${currentProductId}` : ''})`;
      const query = `tag:'${styleHandle}' AND ${ogClause} AND ${availabilityQuery}`;
      const response = await this.execute(this.queries.productsQuery(selections), { query });
      const products = response?.products?.nodes ?? [];

      return products
        .filter(product => {
          if (excludeFutureDrops && isFutureDrop(product.dropDateTime)) return false;
          if (excludeLowAvailability && product.variants?.nodes?.length > 6) { // 6 varaints to exclude accoceries
            const total = product.variants.nodes.length;
            const unavailable = product.variants.nodes.filter(v => !v.availableForSale).length;
            if (unavailable / total > 0.5) return false;
          }
          // The query requests the PDP's current product explicitly; keep this check as defense in depth
          return (currentProductId && product.id.includes(currentProductId)) || product.availableForSale || product.tags.includes('BACK-IN-STOCK') || product.tags.includes('COMING-SOON');
        })
        .sort((a, b) => {
          // 1. Sort by availability
          if (a.availableForSale !== b.availableForSale) {
            return a.availableForSale ? -1 : 1;
          }

          // 2. Sort by price (higher prices first = less discounted prioritized)
          const aPrice = a.priceRange.minVariantPrice.amount;
          const bPrice = b.priceRange.minVariantPrice.amount;
          const priceDiff = bPrice - aPrice;
          if (priceDiff !== 0) {
            return priceDiff;
          }

          // 3. Core product ("Black") is prioritized first after availability and price
          const aColor = a.title.split(' - ').pop()?.toLowerCase();
          const bColor = b.title.split(' - ').pop()?.toLowerCase();

          if (aColor === 'black') return -1;
          if (bColor === 'black') return 1;

          // 4. SKU availability (variant with more available sizes is prioritized)
          if (a.variants?.nodes?.length > 0 && b.variants?.nodes?.length > 0) {
            const aAvailability = a.variants.nodes.filter(variant => variant.availableForSale).length / a.variants.nodes.length;
            const bAvailability = b.variants.nodes.filter(variant => variant.availableForSale).length / b.variants.nodes.length;
            const availabilityDiff = bAvailability - aAvailability;

            if (availabilityDiff !== 0) {
              return availabilityDiff;
            }
          }

          // 5. If the availability is the same, then the default case (no change in order)
          return 0;
        })
        .map(product => {
          const swatch = product.tags.find(tag => tag.startsWith('colourSwatch:'))
          let colourNamePrint, swatchColor

          if (swatch) {
            const [_, colour] = swatch.split(':');
            if (colour) {
              const [swatchName, primaryColour, ...secondaryColours] = colour.split('|');
              colourNamePrint = swatchName;
              if (primaryColour) {
                // A conic seam through the centre steps 1px when the centre falls exactly on a pixel centre.
                // Two colours use a linear split instead. Other counts move the conic centre 0.01px left and down;
                // moving left keeps a three-colour swatch's top seam on the pixel column it used before.
                if (primaryColour.startsWith('#') && secondaryColours.length === 1) {
                  swatchColor = `linear-gradient(to right, ${primaryColour} 50%, ${secondaryColours[0]} 50%)`;
                } else if (primaryColour.startsWith('#')) {
                  const gradientStops = [primaryColour, ...secondaryColours].reverse().map((hexCode, index) => `${hexCode} 0 ${((index + 1) * (100 / (secondaryColours.length + 1)))}%`).join(', ');
                  swatchColor = `conic-gradient(at calc(50% - 0.01px) calc(50% + 0.01px), ${gradientStops})`;
                } else if (primaryColour.includes('.jpg')) {
                  const image = theme.settings.assetURL.replace('colorplaceholder.jpg', primaryColour);
                  swatchColor = `url(${image}) no-repeat center center / cover`;
                }
              } else {
                swatchColor = swatchName;
              }
            }
          }

          return ({
            id: this.helpers.parseGid(product.id),
            title: product.title,
            styleName: product.title.split(' - ')[0],
            description: product.description,
            details: product.details?.value,
            compareAtPrice: +(product.compareAtPriceRange?.minVariantPrice?.amount) || 0,
            handle: product.handle,
            featuredImage: product.featuredImage?.url,
            dropDateTime: product.dropDateTime?.value,
            colourNamePrint,
            swatchColor,
            collectionHandles: product.collections?.nodes?.map((collection) => collection.handle).filter(Boolean) ?? [],
            price: +(product.priceRange?.minVariantPrice?.amount) || 0,
            currency: product.priceRange?.minVariantPrice?.currencyCode || 'AUD',
            tags: product.tags,
            variants: includeVariants && product.variants?.nodes?.map(variant => ({
              id: this.helpers.parseGid(variant.id),
              sku: variant.sku || '',
              availableForSale: variant.availableForSale,
              selectedOptions: variant.selectedOptions?.map(({ value }) => value) || [],
            })) || [],
          })
        })
    }

    /**
     * Fetches a product by its handle from the Shopify Storefront API.
     *
     * @param {string} handle - The handle of the product to fetch.
     * @return {Promise<Array<{
     *    id: number
     *    available: boolean
     *    dropDateTime: string
     *    title: string
     *    images: Array<string>
     *    variants: Array<{
     *      available: boolean
     *      id: number
     *      title: string
     *    }>
     * }>|null>}
     */
    async fetchProductByHandle(handle) {
      const response = await this.execute(this.queries.productByHandle, {handle})

      const product = response?.product;
      if (!product) return null;
      return {
        id: this.helpers.parseGid(product.id),
        title: product.title,
        price: +(product.priceRange?.minVariantPrice?.amount) || 0,
        compareAtPrice: +(product.compareAtPriceRange?.minVariantPrice?.amount) || 0,
        currency: product.priceRange?.minVariantPrice?.currencyCode || 'AUD',
        tags: product.tags,
        images: product.images?.nodes?.map(image => image.url) || [],
        available: product.availableForSale,
        dropDateTime: product.dropDateTime?.value,
        options: product.options?.map(option => ({ name: option.name, values: option.values ?? [] })) || [],
        variants: product.variants?.nodes?.map(variant => ({
          available: variant.availableForSale,
          id: this.helpers.parseGid(variant.id),
          title: variant.title
        })) || []
      }
    }

    async getTagContentList() {
      const response = await this.execute(this.queries.tagContentList, {
        handle: {
          handle: "tag-content-list",
          type: "tag_content_list"
        }})

      return this.helpers.transformTagContentList(response)
    }

    /**
     * Fetches all `complete_the_set_rule` entries. Unlike the other Storefront reads, this one throws
     * on GraphQL errors or a malformed envelope so the caller can use code defaults.
     *
     * @return {Promise<Array<{tag: string, optionalFilters: Array<string>, extraFilters: string}>>}
     */
    async getCompleteTheSetRules() {
      if (typeof ShopifyStorefrontAPIClient === 'undefined') {
        throw new Error('ShopifyStorefrontAPIClient is not defined');
      }

      const { data, errors } = await this.client.request(this.queries.completeTheSetRules, {
        variables: { countryCode: this.helpers.getCountryCode() }
      });

      if (errors) {
        throw new Error(`complete_the_set_rule query returned errors: ${JSON.stringify(errors)}`);
      }

      return this.helpers.transformCompleteTheSetRules(data)
    }

    /**
     * Fetches all PDP option groups (pockets, length, cup size, …) in one Storefront call.
     * Discovers which OPTION_GROUPS the current product has, then for each group keeps
     * siblings that match every other selected option while letting that group's tag vary.
     *
     * Unavailable siblings are omitted in the Storefront query unless tagged BACK-IN-STOCK or COMING-SOON.
     *
     * @param {string[]} productTags - The tags of the current product.
     * @param {string} optionGroupTag - The grouping tag (brastyle: if present, otherwise YGroup_), e.g. 'brastyle:xxx' or 'YGroup_xxx'
     * @param {string} colorTag - The colorSwatch tag.
     * @param {boolean} isOgProduct - If the product is an OG product.
     * @return {Promise<Array<{
     *    name: string
     *    tagPrefix: string
     *    options: Array<{
     *      id: number
     *      handle: string
     *      title: string
     *      availableForSale: boolean
     *      dimension: string
     *      option: string
     *    }>
     * }>>}
     */
    async fetchAllProductOptions(productTags, optionGroupTag, colorTag, isOgProduct = false) {
      // Determine which option groups the current product has
      const optionGroupSelections = OPTION_GROUPS.reduce((acc, { tagPrefix }) => {
        const matchingTag = productTags.find((tag) => tag.startsWith(tagPrefix));
        if (matchingTag) {
          acc[tagPrefix] = matchingTag;
        }
        return acc;
      }, {});

      if (Object.keys(optionGroupSelections).length === 0) {
        return [];
      }

      const selections = `
        id,
        handle,
        title,
        availableForSale,
        tags,
        variants (first: 25) {
          nodes {
            id
            availableForSale
            selectedOptions {
              name
              value
            }
          }
        }
      `;

      const ogClause = isOgProduct ? `tag:'batch:OG'` : `NOT tag:'batch:OG'`;
      const query = `tag:'${optionGroupTag}' AND tag:'${colorTag}' AND ${ogClause} AND (${PRODUCT_AVAILABILITY_TERMS})`;
      const response = await this.execute(this.queries.productsQuery(selections), { query });
      const products = response?.products?.nodes ?? [];

      // Build option groups from the fetched products
      const optionGroups = OPTION_GROUPS.map(({ name, tagPrefix }) => {
        const selectedTag = optionGroupSelections[tagPrefix];

        // Skip if the current product doesn't have this option type
        if (!selectedTag) return null;

        // Filter products that match all OTHER selected options (let this option vary)
        const options = products
          .filter(({ tags, availableForSale }) => {
            // Must have this option type
            const hasOptionTag = tags.some((tag) => tag.startsWith(tagPrefix));
            if (!hasOptionTag) return false;

            // Skip unavailable products unless they have BACK-IN-STOCK or COMING-SOON
            if (!availableForSale && !tags.includes('BACK-IN-STOCK') && !tags.includes('COMING-SOON')) return false;

            // Must match all OTHER selected options
            return Object.entries(optionGroupSelections).every(([prefix, tag]) => {
              if (prefix === tagPrefix) return true; // Let this option vary
              return tags.includes(tag);
            });
          })
          .map(({ id, handle, title, availableForSale, tags, variants }) => {
            const optionType = tagPrefix.replace(':', '');
            const optionTag = tags.find((tag) => tag.startsWith(tagPrefix));
            const option = optionTag ? optionTag.replace(tagPrefix, '') : '';
            const dimension = this.helpers.getProductDimension(tags, optionType);

            return {
              id: this.helpers.parseGid(id),
              handle,
              title,
              availableForSale,
              dimension,
              option,
              variants: variants?.nodes?.map(variant => ({
                id: this.helpers.parseGid(variant.id),
                availableForSale: variant.availableForSale,
                selectedOptions: variant.selectedOptions?.map(({ value }) => value) || [],
              })) || [],
            };
          })
          // Sort alphabetically first
          .sort((a, b) => a.option.localeCompare(b.option))
          // Then sort by SORTED_LENGTH for length options
          .sort((a, b) => {
            if (name.toLowerCase() === 'length') {
              return SORTED_LENGTH.indexOf(a.option) - SORTED_LENGTH.indexOf(b.option);
            }
            return 0;
          });

        // Only return groups with more than 1 option
        return options.length > 1 ? { name, tagPrefix, options } : null;
      }).filter(Boolean);

      return optionGroups;
    }

    /**
     * Fetches a product with images for product set display.
     * Filters images by alt text containing "Set:true" and sorts by angle (front, side, back).
     * Note: Color swatch data is NOT included here - ProductColorSelector handles its own fetching.
     *
     * @param {string} handle - The handle of the product to fetch.
     * @return {Promise<{
     *    id: number
     *    title: string
     *    styleName: string
     *    handle: string
     *    descriptionHtml: string
     *    available: boolean
     *    price: number
     *    compareAtPrice: number
     *    currency: string
     *    tags: Array<string>
     *    images: Array<{url: string, alt: string, angle: string}>
     *    variants: Array<{id: number, title: string, size: string, available: boolean, price: number, compareAtPrice: number}>
     *    reviewsCount: number
     *    reviewsAverage: number
     * }|null>}
     */
    async fetchProductForSet(handle) {
      const response = await this.execute(this.queries.productForSet, { handle });
      const product = response?.product;
      if (!product) return null;

      // Filter and sort images by Set:true and Angle
      const angleOrder = { front: 0, side: 1, back: 2, closeup: 3 };
      const setImages = (product.images?.nodes ?? [])
        .filter(img => img.altText?.toLowerCase().includes('set:true'))
        .map(img => {
          const alt = img.altText || '';
          const angleMatch = alt.match(/angle:(\w+)/i);
          const angle = angleMatch ? angleMatch[1].toLowerCase() : 'other';
          return {
            url: img.url,
            alt: alt,
            angle: angle,
            width: img.width,
            height: img.height,
          };
        })
        .sort((a, b) => (angleOrder[a.angle] ?? 99) - (angleOrder[b.angle] ?? 99));

      return {
        id: this.helpers.parseGid(product.id),
        title: product.title,
        styleName: product.title.split(' - ')[0],
        handle: product.handle,
        descriptionHtml: product.descriptionHtml,
        details: product.details?.value || null,
        available: product.availableForSale,
        price: +(product.priceRange?.minVariantPrice?.amount) || 0,
        compareAtPrice: +(product.compareAtPriceRange?.minVariantPrice?.amount) || 0,
        currency: product.priceRange?.minVariantPrice?.currencyCode || 'AUD',
        tags: product.tags,
        images: setImages,
        variants: (product.variants?.nodes ?? []).map(variant => ({
          id: this.helpers.parseGid(variant.id),
          title: variant.title,
          size: variant.selectedOptions?.find(opt => opt.name.toLowerCase() === 'size')?.value || variant.title,
          options: variant.selectedOptions?.map(opt => opt.value) || [],
          available: variant.availableForSale,
          price: +(variant.price?.amount) || 0,
          compareAtPrice: variant.compareAtPrice ? +(variant.compareAtPrice.amount) : null,
        })),
        reviewsCount: product.reviewsCount?.value ? +product.reviewsCount.value : 0,
        reviewsAverage: product.reviewsAverage?.value ? +product.reviewsAverage.value : 0,
      };
    }

    /**
     * Fetches a variant by selected options from the Shopify Storefront API.
     * @param id - product id
     * @param selectedOptions
     * @returns {Promise<{id: number, available: boolean}|null>}
     */
    async fetchVariantBySelectedOptions(id, selectedOptions) {
      const response = await this.execute(this.queries.variantBySelectedOptions, { id: `gid://shopify/Product/${id}`, selectedOptions });
      const variant = response?.product?.variantBySelectedOptions;
      return variant ? {
        id: this.helpers.parseGid(variant.id),
        available: variant.availableForSale,
        sku: variant.sku,
        inventory_management: 'shopify',
        price: Number(variant.price.amount) * 100,
        compare_at_price: variant.compareAtPrice ? Number(variant.compareAtPrice.amount) * 100 : null,
        options: variant.selectedOptions.map(({ value }) => (value)),
        option1: variant.selectedOptions[0]?.value || null,
        option2: variant.selectedOptions[1]?.value || null,
        option3: variant.selectedOptions[2]?.value || null,
      } : null;
    }

    /**
     * Fetches structured size chart content for a size_chart metaobject handle.
     *
     * @param {string} handle
     * @return {Promise<{content: Object, handle: string, title: string}|null>}
     */
    async fetchSizeChart(handle) {
      const response = await this.execute(this.queries.sizeChartByHandle, {
        handle: { handle, type: 'size_chart' }
      });
      const metaobject = response?.metaobject;
      const rawContent = metaobject?.content?.value;
      if (!rawContent) return null;

      try {
        const content = JSON.parse(rawContent);
        if (content?.schemaVersion !== 1 || !Array.isArray(content.panels)) return null;

        return {
          content,
          handle: metaobject.handle,
          title: metaobject.title?.value || 'Size guide'
        };
      } catch (error) {
        console.error(`Invalid size_chart content for ${handle}`, error);
        return null;
      }
    }

    /**
     * B2B store-fulfillment: fetch live custom.b2b_available_qty for a set of
     * variant GIDs. Used by the cart to classify lines against fresh data (the Liquid
     * snapshot only covers items present at page load; AJAX adds/qty changes are not in it).
     * Returns a map { "gid://shopify/ProductVariant/123": <int> | null }.
     * @param {string[]} variantGids
     * @return {Promise<Record<string, number|null>>}
     */
    async fetchB2bAvailability(variantGids = []) {
      const ids = [...new Set(variantGids)].filter(Boolean);
      if (ids.length === 0) return {};
      const response = await this.execute(this.queries.b2bAvailability, { ids });
      const map = {};
      for (const node of response?.nodes || []) {
        if (!node || !node.id) continue;
        const raw = node.metafield && node.metafield.value;
        map[node.id] = raw == null || raw === '' ? null : Number(raw);
      }
      return map;
    }

    async execute(query, variables) {
      if(typeof ShopifyStorefrontAPIClient === 'undefined') {
          console.error('ShopifyStorefrontAPIClient is not defined');
          return;
      }
      const { data, errors } = await this.client.request(query, {
        variables : {
          ...variables,
          countryCode: this.helpers.getCountryCode()
        }
      });

      errors && console.error('ShopifyStorefrontAPIClient ERRORS', errors);

      return data
    }

    queries = {
      b2bAvailability: `
        query b2bAvailability($ids: [ID!]!) {
          nodes(ids: $ids) {
            ... on ProductVariant {
              id
              metafield(namespace: "custom", key: "b2b_available_qty") {
                value
              }
            }
          }
        }
      `,
      productsQuery: (selections) => `
        query productsQuery($query: String!, $countryCode: CountryCode) @inContext(country: $countryCode){
          products(
            query: $query
            first: 100
            sortKey: CREATED_AT
            reverse: true
          ) {
            nodes {
              ${selections}
            }
          }
        }
      `,
      // TODO: Revisit this cap for TUP/high-variant quick add coverage.
      productByHandle:  `
        query getProductByHandle($handle: String!, $countryCode: CountryCode) @inContext(country: $countryCode){
          product(handle: $handle) {
            id
            title
            availableForSale
            tags
            dropDateTime: metafield(key: "datetime", namespace: "drop") {
              value
            }
            images(first: 6) {
              nodes {
                url
              }
            }
            priceRange {
              minVariantPrice {
                amount
                currencyCode
              }
            }
            compareAtPriceRange {
              minVariantPrice {
                amount
                currencyCode
              }
            }
            options {
              name
              values
            }
            variants(first: 20) {
              nodes {
                availableForSale
                id
                title
              }
            }
          }
        }
      `,
      tagContentList: `
        query getTagContentList($handle: MetaobjectHandleInput!, $countryCode: CountryCode) @inContext(country: $countryCode){
          metaobject(handle: $handle) {
            field(key: "contents") {
              references(first: 100) {
                nodes {
                  ... on Metaobject {
                    tag: field(key: "tag") {
                      value
                    }
                    exclude_tags: field(key: "exclude_tags") {
                      value
                    }
                    channel: field(key: "channel") {
                      value
                    }
                    markets: field(key: "markets") {
                      value
                    }
                    pdp_below_product_options: field(key: "pdp_above_atc") {
                      references(first: 10) {
                        nodes {
                          ...Fields
                        }
                      }
                    }
                    pdp_designed_for: field(key: "pdp_above_description") {
                      references(first: 10) {
                        nodes {
                          ...Fields
                        }
                      }
                    }
                    pdp_below_product_options_2: field(key: "pdp_below_atc") {
                      references(first: 10) {
                        nodes {
                          ...Fields
                        }
                      }
                    }
                    pdp_below_description: field(key: "pdp_below_price") {
                      references(first: 10) {
                        nodes {
                          ...Fields
                        }
                      }
                    }
                    pdp_above_review: field(key: "pdp_above_review") {
                      references(first: 10) {
                        nodes {
                          ...Fields
                        }
                      }
                    }
                    pdp_top_left_in_image: field(key: "pdp_below_review") {
                      references(first: 10) {
                        nodes {
                          ...Fields
                        }
                      }
                    }
                    plp_below: field(key: "plp_below") {
                      references(first: 10) {
                        nodes {
                          ...Fields
                        }
                      }
                    }
                    plp_bottom_left: field(key: "plp_bottom_left") {
                      references(first: 10) {
                        nodes {
                          ...Fields
                        }
                      }
                    }
                    plp_top_left: field(key: "plp_top_left") {
                      references(first: 10) {
                        nodes {
                          ...Fields
                        }
                      }
                    }
                    pdp_below_details: field(key: "pdp_below_details") {
                      references(first: 10) {
                        nodes {
                          ...Fields
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }

        fragment Fields on Metaobject {
          id
          type
          fields {
            key
            value
            imageData: reference {
              ... on MediaImage {
                image {
                  altText
                  url
                  width
                  height
                }
              }
            }
          }
        }
      `,
      completeTheSetRules: `
        query getCompleteTheSetRules($countryCode: CountryCode) @inContext(country: $countryCode){
          metaobjects(type: "complete_the_set_rule", first: 250) {
            nodes {
              tag: field(key: "tag") {
                value
              }
              optional_filters: field(key: "optional_filters") {
                value
              }
              extra_filters: field(key: "extra_filters") {
                value
              }
            }
          }
        }
      `,
      variantBySelectedOptions: `
        query getVariantBySelectedOptions($id: ID!, $selectedOptions: [SelectedOptionInput!]!, $countryCode: CountryCode) @inContext(country: $countryCode){
          product(id: $id) {
            variantBySelectedOptions(selectedOptions: $selectedOptions) {
              id
              availableForSale
              sku
              price {
                amount
              }
              compareAtPrice {
                amount
              }
              selectedOptions {
                name
                value
              }
            }
          }
        }
      `,
      sizeChartByHandle: `
        query getSizeChartByHandle($handle: MetaobjectHandleInput!, $countryCode: CountryCode) @inContext(country: $countryCode){
          metaobject(handle: $handle) {
            handle
            title: field(key: "title") {
              value
            }
            content: field(key: "content") {
              value
            }
          }
        }
      `,
      productForSet: `
        query getProductForSet($handle: String!, $countryCode: CountryCode) @inContext(country: $countryCode){
          product(handle: $handle) {
            id
            title
            handle
            descriptionHtml
            tags
            availableForSale
            priceRange {
              minVariantPrice {
                amount
                currencyCode
              }
            }
            compareAtPriceRange {
              minVariantPrice {
                amount
              }
            }
            details: metafield(namespace: "custom", key: "details") {
              value
            }
            reviewsCount: metafield(namespace: "yotpo", key: "reviews_count") {
              value
            }
            reviewsAverage: metafield(namespace: "yotpo", key: "reviews_average") {
              value
            }
            images(first: 50) {
              nodes {
                url
                altText
                width
                height
              }
            }
            variants(first: 25) {
              nodes {
                id
                title
                availableForSale
                price {
                  amount
                }
                compareAtPrice {
                  amount
                }
                selectedOptions {
                  name
                  value
                }
              }
            }
          }
        }
      `
    }

    helpers = {
      parseGid: (gId) => +(gId?.split('/').pop()) || null,
      getCountryCode: () => window.Shopify?.country ?? 'AU',
      /**
       * Transforms the tag content list response from array to a  key value object with flattened sections contents
       */
      transformTagContentList: (data) => (data?.metaobject?.field?.references?.nodes ?? []).reduce((acc, node, index) => {
        if (!node.tag?.value) return acc;
        const { tag, exclude_tags, channel, markets, ...sectionsContents } = node;
        // Filter out the tags that are not available in the current market
        const availableMarkets = markets?.value ? JSON.parse(markets.value) : null;
        if (availableMarkets && !availableMarkets.includes(TAG_CONTENT_MARKET_CODE)) return acc;

        // Filter out the tags that are only available on mobile app
        if (channel?.value === 'Tapcart') return acc;

        return {
          ...acc,
          [tag.value]: {
            sortOrder: index,
            excludeTags: exclude_tags ? JSON.parse(exclude_tags.value) : [],
            ...this.helpers.flattenSectionsContents(sectionsContents)
          }
        };
      }, {}),
      /** Malformed envelope throws. One unreadable entry is dropped whole, extra_filters included. */
      transformCompleteTheSetRules: (data) => {
        const nodes = data?.metaobjects?.nodes;
        if (!Array.isArray(nodes)) {
          throw new Error('complete_the_set_rule query returned a malformed response');
        }

        return nodes.reduce((acc, node) => {
          const tag = node?.tag?.value;
          if (!tag) return acc;

          let optionalFilters = [];
          if (node.optional_filters?.value) {
            try {
              optionalFilters = JSON.parse(node.optional_filters.value);
            } catch (error) {
              console.error(`Invalid complete_the_set_rule optional_filters for tag ${tag}, skipping the rule`, error);
              return acc;
            }

            if (!Array.isArray(optionalFilters)) {
              console.error(`complete_the_set_rule optional_filters for tag ${tag} is not a list, skipping the rule`);
              return acc;
            }
          }

          acc.push({
            tag,
            optionalFilters,
            extraFilters: node.extra_filters?.value ?? ''
          });

          return acc;
        }, []);
      },
      /**
       * Flattens the sections contents to a single level object
       */
      flattenSectionsContents: (sectionsContents) => Object.keys(sectionsContents).reduce((acc, key) => ({
        ...acc,
        [key]: sectionsContents[key]?.references?.nodes ?? []
      }), {}),

      getProductDimension: (tags, optionType) => {
        const tag = tags.find((tag) => tag.startsWith(`dimensions_${optionType}:`))
        return tag ? tag.split(`dimensions_${optionType}:`).pop() : ''
      }
    }
  }

  (window.ShopifyStoreFrontAPI = new ShopifyStoreFrontAPI()).getTagContentList().then((tagContentList) => {
    window.getTagsContents = (tags) => tags
      .reduce((acc, tag) => {
        if (tagContentList[tag]) {
          acc.push(tagContentList[tag]);
        }
        return acc;
      }, [])
      .filter(({ excludeTags }) => !excludeTags || !excludeTags.some((excludedTag) => tags.includes(excludedTag)))
      .sort(({ sortOrder: a }, { sortOrder: b }) => a - b);

    // console.info("%c✅ Tag Content Loaded", "color:#48DA6EFF; font-size:16px;");
    document.dispatchEvent(new CustomEvent("TagContentLoaded"));
  });
})()
